import { memo, useState } from 'react';
import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import moment from 'moment';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useMenu from 'hooks/useMenu';
import CurrencyFormat from 'components/ui/text-formats/CurrencyFormat';
import PhoneFormat from 'components/ui/text-formats/PhoneFormat';
import TaskEditModal from './TaskEditModal';
import TaskCommentModal from './TaskCommentModal';
import TaskFileModal from './TaskFileModal';

const STATUS_ACTIVE = 1;
const STATUS_COMPLETE = 2;

const statusChip = (status, t) => {
  if (status === STATUS_ACTIVE)
    return (
      <Chip
        label={t('tasks.staffTask.status.active')}
        size='small'
        color='success'
        variant='outlined'
      />
    );
  if (status === STATUS_COMPLETE)
    return (
      <Chip
        label={t('tasks.staffTask.status.complete')}
        size='small'
        color='primary'
        variant='outlined'
      />
    );
  return (
    <Chip
      label={t('tasks.staffTask.status.inactive')}
      size='small'
      color='default'
      variant='outlined'
    />
  );
};

const ContractTasksTab = ({ tasks = [], refetchFn }) => {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();
  const { hasChild } = useMenu();

  const canEdit = hasChild('STAFF_TASKS', 'STAFF_EDIT_TASKS');
  const canDelete = hasChild('STAFF_TASKS', 'STAFF_DELETE_TASKS');

  const [editOpen, setEditOpen] = useState(false);
  const [commentOpen, setCommentOpen] = useState(false);
  const [fileOpen, setFileOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false);
  const [activeTask, setActiveTask] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);

  const openEdit = (task) => {
    setActiveTask(task);
    setEditOpen(true);
  };

  const openComment = (task) => {
    setActiveTask(task);
    setCommentOpen(true);
  };

  const openFile = (task) => {
    setActiveTask(task);
    setFileOpen(true);
  };

  const openComplete = (task) => {
    setActiveTask(task);
    setConfirmCompleteOpen(true);
  };

  const openDelete = (task) => {
    setActiveTask(task);
    setConfirmDeleteOpen(true);
  };

  const handleComplete = async () => {
    if (!activeTask?.id) return;
    setLoading(true);
    try {
      await axiosPrivate.post(`/admin/task-staff/complete/${activeTask.id}`);
      refetchFn();
    } finally {
      setLoading(false);
      setConfirmCompleteOpen(false);
      setActiveTask(null);
    }
  };

  const handleDelete = async () => {
    if (!activeTask?.id) return;
    setLoading(true);
    try {
      await axiosPrivate.post(`/admin/task-staff/delete/${activeTask.id}`);
      refetchFn();
    } finally {
      setLoading(false);
      setConfirmDeleteOpen(false);
      setActiveTask(null);
    }
  };

  if (!tasks?.length) {
    return (
      <div className='flex items-center justify-center py-8 text-gray-400'>
        <i className='bi bi-exclamation-octagon text-xl mr-2' />
        {t('common.global.noDataFound')}
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-2 mt-2'>
      {tasks.map((task) => (
        <div
          key={task.id}
          className='flex flex-col rounded-xl border border-gray-200 bg-white px-4 py-3 gap-2'
        >
          {/* Top row: info + actions */}
          <div className='flex items-start justify-between gap-4'>
            <div className='flex flex-col gap-1 min-w-0 flex-1'>
              <div className='flex items-center gap-2 flex-wrap'>
                <span className='font-medium text-sm text-gray-800 truncate'>
                  {task.title || `#${task.id}`}
                </span>
                {statusChip(task.status, t)}
              </div>
              <div className='flex items-center gap-4 text-xs text-gray-500 flex-wrap'>
                {task.user?.name && (
                  <span>
                    <i className='bi bi-person mr-1' />
                    {task.user.name}
                  </span>
                )}
                {task.type?.name && (
                  <span>
                    <i className='bi bi-tag mr-1' />
                    {task.type.name}
                  </span>
                )}
                {task.date && (
                  <span>
                    <i className='bi bi-calendar mr-1' />
                    {moment(task.date).format('DD.MM.YYYY')}
                  </span>
                )}
                {task.till_date && (
                  <span
                    className={
                      task.status === STATUS_ACTIVE &&
                      moment(task.till_date).isBefore(moment(), 'day')
                        ? 'text-red-500 font-medium'
                        : ''
                    }
                  >
                    <i className='bi bi-calendar-x mr-1' />
                    {moment(task.till_date).format('DD.MM.YYYY')}
                  </span>
                )}
                {task.date_complete && (
                  <span className='text-green-600'>
                    <i className='bi bi-check-circle mr-1' />
                    {moment(task.date_complete).format('DD.MM.YYYY')}
                  </span>
                )}
              </div>
            </div>

            {task.status === STATUS_ACTIVE && (
              <div className='flex items-center gap-1 shrink-0'>
                {canEdit && (
                  <>
                    <Tooltip title={t('tasks.staffTask.editModal.title')}>
                      <IconButton size='small' onClick={() => openEdit(task)}>
                        <i className='bi bi-pencil-square text-sm text-blue-500' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('tasks.staffTask.completeModal.title')}>
                      <IconButton size='small' onClick={() => openComplete(task)}>
                        <i className='bi bi-check-circle text-sm text-green-500' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('tasks.staffTask.commentModal.title')}>
                      <IconButton size='small' onClick={() => openComment(task)}>
                        <i className='bi bi-chat-left-dots text-sm text-gray-500' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('tasks.staffTask.fileModal.title')}>
                      <IconButton size='small' onClick={() => openFile(task)}>
                        <i className='bi bi-paperclip text-sm text-gray-500' />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
                {canDelete && (
                  <Tooltip title={t('tasks.staffTask.deleteModal.title')}>
                    <IconButton size='small' onClick={() => openDelete(task)}>
                      <i className='bi bi-trash text-sm text-red-500' />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            )}
          </div>

          {/* Contract info (if debt-related) */}
          {task.contract?.id && (
            <div className='mt-1 flex flex-col gap-1 border-t border-gray-100 pt-2 bg-amber-50 -mx-4 -mb-3 px-4 py-2 rounded-b-xl'>
              <div className='flex flex-wrap items-center gap-x-3 gap-y-1 text-xs'>
                <Link
                  to={`/admin/contract/view/${task.contract.id}`}
                  className='font-semibold text-blue-600 no-underline hover:underline'
                >
                  <i className='bi bi-file-earmark-text mr-1' />
                  №{task.contract.name}
                </Link>
                {task.contract.custom && (
                  <Link
                    to={`/admin/client/view/${task.contract.custom.id}`}
                    className='text-gray-700 no-underline hover:underline'
                  >
                    <i className='bi bi-person mr-1' />
                    {task.contract.custom.surname} {task.contract.custom.name}
                  </Link>
                )}
                {task.contract.custom?.phone && (
                  <a
                    href={`tel:${task.contract.custom.phone}`}
                    className='text-gray-700 no-underline hover:underline'
                  >
                    <i className='bi bi-telephone mr-1' />
                    <PhoneFormat value={task.contract.custom.phone} />
                  </a>
                )}
                {task.contract.homes?.[0]?.blocks?.objects?.name && (
                  <span className='text-gray-600'>
                    <i className='bi bi-building mr-1' />
                    {task.contract.homes[0].blocks.objects.name}
                    {task.contract.homes[0].number
                      ? ` · №${task.contract.homes[0].number}`
                      : ''}
                  </span>
                )}
              </div>
              <div className='flex flex-wrap items-center gap-x-3 gap-y-1 text-xs'>
                {task.contract.overdue_sum > 0 && (
                  <span className='text-red-600 font-medium'>
                    <i className='bi bi-exclamation-circle mr-1' />
                    {t('tasks.staffTask.contract.overdue')}:{' '}
                    <CurrencyFormat
                      value={task.contract.overdue_sum}
                      suffix={task.contract.isvalute == '1' ? ' $' : ' UZS'}
                      decimalScale={0}
                    />
                    {task.contract.overdue_count
                      ? ` (${task.contract.overdue_count})`
                      : ''}
                  </span>
                )}
                {task.contract.left != null && (
                  <span className='text-gray-700'>
                    <i className='bi bi-cash-coin mr-1' />
                    {t('tasks.staffTask.contract.left')}:{' '}
                    <CurrencyFormat
                      value={task.contract.left}
                      suffix={task.contract.isvalute == '1' ? ' $' : ' UZS'}
                      decimalScale={0}
                    />
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Files */}
          {task.files?.length > 0 && (
            <div className='mt-1 flex flex-col gap-1 border-t border-gray-100 pt-2'>
              <span className='text-xs font-medium text-gray-400 uppercase tracking-wide'>
                <i className='bi bi-paperclip mr-1' />
                {t('tasks.staffTask.fileModal.title')} ({task.files.length})
              </span>
              {/* Image files — horizontal row */}
              {task.files.some((f) => /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(f.file || '')) && (
                <div className='flex flex-row gap-2 flex-wrap'>
                  {task.files
                    .filter((f) => /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(f.file || ''))
                    .map((file) => {
                      const fileUrl = `${process.env.REACT_APP_BACKEND_URL}/${file.file}`;
                      return (
                        <button key={file.id} type='button' onClick={() => setPreviewImg(fileUrl)} className='flex flex-col gap-1 items-center'>
                          <img
                            src={fileUrl}
                            alt={file.title || ''}
                            className='h-24 w-24 rounded-lg object-cover border border-gray-200 hover:opacity-80 transition-opacity'
                          />
                          {file.title && (
                            <span className='text-xs text-gray-500 truncate max-w-[96px]'>{file.title}</span>
                          )}
                        </button>
                      );
                    })}
                </div>
              )}
              {/* Non-image files — list */}
              {task.files
                .filter((f) => !/\.(jpg|jpeg|png|gif|webp|svg)$/i.test(f.file || ''))
                .map((file) => {
                  const fileUrl = file.file ? `${process.env.REACT_APP_BACKEND_URL}/${file.file}` : null;
                  return (
                    <div key={file.id} className='flex items-center gap-2 text-xs bg-gray-50 rounded-lg px-3 py-2'>
                      <i className='bi bi-file-earmark text-gray-400 text-sm shrink-0' />
                      <span className='font-medium text-gray-700 truncate flex-1'>
                        {file.title || file.file?.split('/').pop()}
                      </span>
                      <span className='text-gray-600'>
                        <i className='bi bi-person mr-1' />
                        {file.user?.name || '—'}
                      </span>
                      {file.date && (
                        <span className='text-gray-500'>{moment(file.date).format('DD.MM.YYYY')}</span>
                      )}
                      {fileUrl && (
                        <a href={fileUrl} target='_blank' rel='noreferrer' className='text-blue-500 hover:text-blue-700 shrink-0'>
                          <i className='bi bi-download text-sm' />
                        </a>
                      )}
                    </div>
                  );
                })}
            </div>
          )}

          {/* Comments */}
          {task.comments?.length > 0 && (
            <div className='mt-1 flex flex-col gap-1 border-t border-gray-100 pt-2'>
              <span className='text-xs font-medium text-gray-400 uppercase tracking-wide'>
                <i className='bi bi-chat-left-text mr-1' />
                {t('tasks.staffTask.commentModal.title')} ({task.comments.length})
              </span>
              {task.comments.map((comment) => (
                <div key={comment.id} className='flex gap-2 text-xs bg-gray-50 rounded-lg px-3 py-2'>
                  <div className='flex flex-col flex-1 gap-0.5'>
                    <div className='flex items-center gap-2 text-gray-500'>
                      <span className='font-medium text-gray-700'>
                        <i className='bi bi-person mr-1' />
                        {comment.user?.name || '—'}
                      </span>
                      {comment.date && (
                        <span>{moment(comment.date).format('DD.MM.YYYY')}</span>
                      )}
                    </div>
                    <p className='text-gray-700 whitespace-pre-wrap'>{comment.text}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Edit modal */}
      {editOpen && (
        <TaskEditModal
          open={editOpen}
          setOpen={setEditOpen}
          task={activeTask}
          setTask={setActiveTask}
          refetchFn={refetchFn}
        />
      )}

      {/* Comment modal */}
      {commentOpen && (
        <TaskCommentModal
          open={commentOpen}
          setOpen={setCommentOpen}
          taskId={activeTask?.id}
          refetchFn={refetchFn}
        />
      )}

      {/* File modal */}
      {fileOpen && (
        <TaskFileModal
          open={fileOpen}
          setOpen={setFileOpen}
          taskId={activeTask?.id}
          refetchFn={refetchFn}
        />
      )}

      {/* Complete confirm */}
      <Dialog
        open={confirmCompleteOpen}
        onClose={() => setConfirmCompleteOpen(false)}
        maxWidth='xs'
        fullWidth
        PaperProps={{ className: '!base-border !shadow-none' }}
      >
        <DialogTitle className='!bg-gray-100 text-center'>
          {t('tasks.staffTask.completeModal.title')}
          <p className='text-sm font-normal text-gray-500 mt-1'>
            {t('tasks.staffTask.completeModal.description')}
          </p>
        </DialogTitle>
        <DialogActions className='!justify-center !pb-4 gap-2'>
          <Button
            variant='outlined'
            color='inherit'
            onClick={() => setConfirmCompleteOpen(false)}
          >
            {t('common.button.cancel')}
          </Button>
          <Button
            variant='contained'
            color='success'
            onClick={handleComplete}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={14} /> : null}
          >
            {t('common.button.yes')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        maxWidth='xs'
        fullWidth
        PaperProps={{ className: '!base-border !shadow-none' }}
      >
        <DialogTitle className='!bg-gray-100 text-center'>
          {t('tasks.staffTask.deleteModal.title')}
          <p className='text-sm font-normal text-gray-500 mt-1'>
            {t('tasks.staffTask.deleteModal.description')}
          </p>
        </DialogTitle>
        <DialogActions className='!justify-center !pb-4 gap-2'>
          <Button
            variant='outlined'
            color='inherit'
            onClick={() => setConfirmDeleteOpen(false)}
          >
            {t('common.button.cancel')}
          </Button>
          <Button
            variant='contained'
            color='error'
            onClick={handleDelete}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={14} /> : null}
          >
            {t('common.button.yes')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image preview popup */}
      <Dialog
        open={!!previewImg}
        onClose={() => setPreviewImg(null)}
        maxWidth='lg'
        PaperProps={{ className: '!bg-transparent !shadow-none', style: { margin: 8 } }}
      >
        <div className='relative'>
          <IconButton
            size='small'
            onClick={() => setPreviewImg(null)}
            className='!absolute !top-2 !right-2 !bg-black/50 !text-white hover:!bg-black/70'
          >
            <i className='bi bi-x text-xl' />
          </IconButton>
          <img
            src={previewImg}
            alt=''
            className='max-w-[90vw] max-h-[90vh] rounded-xl object-contain'
          />
        </div>
      </Dialog>
    </div>
  );
};

export default memo(ContractTasksTab);
